import React, { useState, useEffect } from 'react';
import SMSF_RATES_CONFIG from './SMSF_Rates_Config.js';
import { formatInputValue, getNumericInputProps } from './utils/inputHelpers.js';

const SMSFEnhancedComplianceDashboard = ({ smsfState, setSmsfState }) => {
    const [activeView, setActiveView] = useState('dashboard');
    const [complianceStatus, setComplianceStatus] = useState({});
    const [upcomingDeadlines, setUpcomingDeadlines] = useState([]);
    const [complianceScore, setComplianceScore] = useState(0);
    
    // Custom Rules
    const [customRules, setCustomRules] = useState([]);
    const [showAddRule, setShowAddRule] = useState(false);
    const [newRule, setNewRule] = useState({
        name: '',
        type: 'threshold',
        threshold: 0,
        condition: 'greater_than',
        alert: true
    });
    
    // Email Alerts
    const [emailAlerts, setEmailAlerts] = useState({
        enabled: false,
        email: '',
        breachAlerts: true,
        warningAlerts: true,
        monthlyReport: false
    });

    // Compliance categories and their status
    const complianceCategories = {
        contribution_caps: {
            name: 'Contribution Caps',
            description: 'Annual contribution limits and carry-forward provisions',
            weight: 25,
            status: 'compliant',
            lastChecked: '2024-12-15',
            issues: []
        },
        transfer_balance_cap: {
            name: 'Transfer Balance Cap',
            description: 'Retirement phase transfer limits',
            weight: 20,
            status: 'compliant',
            lastChecked: '2024-12-15',
            issues: []
        },
        in_house_assets: {
            name: 'In-House Assets',
            description: 'Related party transactions and asset restrictions',
            weight: 20,
            status: 'warning',
            lastChecked: '2024-12-15',
            issues: ['Property rental to related party requires market rent verification']
        },
        investment_strategy: {
            name: 'Investment Strategy',
            description: 'Investment policy and diversification requirements',
            weight: 15,
            status: 'compliant',
            lastChecked: '2024-12-15',
            issues: []
        },
        record_keeping: {
            name: 'Record Keeping',
            description: 'Documentation and record retention requirements',
            weight: 10,
            status: 'compliant',
            lastChecked: '2024-12-15',
            issues: []
        },
        reporting: {
            name: 'ATO Reporting',
            description: 'Annual returns and member statements',
            weight: 10,
            status: 'compliant',
            lastChecked: '2024-12-15',
            issues: []
        }
    };

    // ATO deadlines and requirements
    const atoRequirements = [
        {
            id: 1,
            title: 'Annual Return Due',
            description: 'SMSF Annual Return for 2023-24 financial year',
            dueDate: '2025-05-15',
            type: 'critical',
            status: 'pending',
            daysRemaining: 150,
            penalty: '$210 per penalty unit'
        },
        {
            id: 2,
            title: 'Member Statements',
            description: 'Annual member benefit statements',
            dueDate: '2025-05-15',
            type: 'important',
            status: 'pending',
            daysRemaining: 150,
            penalty: '$210 per penalty unit'
        },
        {
            id: 3,
            title: 'Audit Completion',
            description: 'Annual audit report completion',
            dueDate: '2025-05-15',
            type: 'important',
            status: 'pending',
            daysRemaining: 150,
            penalty: 'Late lodgement penalties'
        },
        {
            id: 4,
            title: 'Contribution Cap Review',
            description: 'Review and document contribution cap usage',
            dueDate: '2025-06-30',
            type: 'routine',
            status: 'pending',
            daysRemaining: 197,
            penalty: 'Excess contributions tax'
        },
        {
            id: 5,
            title: 'Investment Strategy Review',
            description: 'Annual investment strategy review and update',
            dueDate: '2025-06-30',
            type: 'routine',
            status: 'pending',
            daysRemaining: 197,
            penalty: 'Compliance notice'
        }
    ];

    // Compliance calendar events
    const complianceCalendar = [
        {
            id: 1,
            title: 'Monthly Bank Reconciliation',
            description: 'Reconcile all bank accounts and investment statements',
            frequency: 'monthly',
            nextDue: '2025-01-15',
            completed: true
        },
        {
            id: 2,
            title: 'Quarterly Investment Review',
            description: 'Review investment performance and rebalance if needed',
            frequency: 'quarterly',
            nextDue: '2025-03-31',
            completed: false
        },
        {
            id: 3,
            title: 'Annual Valuation Update',
            description: 'Update asset valuations and document valuation methods',
            frequency: 'annually',
            nextDue: '2025-06-30',
            completed: false
        },
        {
            id: 4,
            title: 'Trustee Minutes',
            description: 'Document trustee decisions and meetings',
            frequency: 'as_needed',
            nextDue: '2025-01-15',
            completed: false
        }
    ];

    // Calculate overall compliance score
    const calculateComplianceScore = () => {
        let totalScore = 0;
        let totalWeight = 0;

        Object.entries(complianceCategories).forEach(([key, category]) => {
            const statusScore = category.status === 'compliant' ? 100 :
                               category.status === 'warning' ? 75 :
                               category.status === 'non_compliant' ? 25 : 0;
            
            totalScore += statusScore * category.weight;
            totalWeight += category.weight;
        });

        return totalWeight > 0 ? Math.round(totalScore / totalWeight) : 0;
    };

    // Get compliance status color
    const getStatusColor = (status) => {
        switch (status) {
            case 'compliant': return 'text-green-600 bg-green-100';
            case 'warning': return 'text-yellow-600 bg-yellow-100';
            case 'non_compliant': return 'text-red-600 bg-red-100';
            default: return 'text-gray-600 bg-gray-100';
        }
    };

    // Get deadline urgency color
    const getUrgencyColor = (daysRemaining, type) => {
        if (daysRemaining <= 30 && type === 'critical') return 'text-red-600 bg-red-100';
        if (daysRemaining <= 60 && type === 'critical') return 'text-orange-600 bg-orange-100';
        if (daysRemaining <= 30 && type === 'important') return 'text-orange-600 bg-orange-100';
        if (daysRemaining <= 90 && type === 'important') return 'text-yellow-600 bg-yellow-100';
        return 'text-blue-600 bg-blue-100';
    };

    useEffect(() => {
        const score = calculateComplianceScore();
        setComplianceScore(score);
    }, [complianceCategories]);

    const renderDashboard = () => (
        <div className="space-y-6">
            {/* Compliance Score Overview */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Compliance Overview</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="text-center p-6 bg-blue-50 rounded-lg">
                        <div className="text-4xl font-bold text-blue-600 mb-2">
                            {complianceScore}%
                        </div>
                        <div className="text-sm text-blue-700">Overall Compliance Score</div>
                    </div>
                    <div className="text-center p-6 bg-green-50 rounded-lg">
                        <div className="text-4xl font-bold text-green-600 mb-2">
                            {Object.values(complianceCategories).filter(c => c.status === 'compliant').length}
                        </div>
                        <div className="text-sm text-green-700">Compliant Areas</div>
                    </div>
                    <div className="text-center p-6 bg-orange-50 rounded-lg">
                        <div className="text-4xl font-bold text-orange-600 mb-2">
                            {Object.values(complianceCategories).filter(c => c.status === 'warning' || c.status === 'non_compliant').length}
                        </div>
                        <div className="text-sm text-orange-700">Areas Needing Attention</div>
                    </div>
                </div>
            </div>

            {/* Compliance Categories */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Compliance Categories</h3>
                <div className="space-y-4">
                    {Object.entries(complianceCategories).map(([key, category]) => (
                        <div key={key} className="border border-gray-200 rounded-lg p-4">
                            <div className="flex justify-between items-start mb-2">
                                <div>
                                    <h4 className="font-semibold text-gray-800">{category.name}</h4>
                                    <p className="text-sm text-gray-600">{category.description}</p>
                                </div>
                                <span className={`px-3 py-1 text-xs rounded-full ${getStatusColor(category.status)}`}>
                                    {category.status.replace('_', ' ').toUpperCase()}
                                </span>
                            </div>
                            {category.issues.length > 0 && (
                                <div className="mt-2">
                                    {category.issues.map((issue, index) => (
                                        <div key={index} className="text-sm text-orange-600 bg-orange-50 p-2 rounded">
                                            ⚠️ {issue}
                                        </div>
                                    ))}
                                </div>
                            )}
                            <div className="mt-2 text-xs text-gray-500">
                                Last checked: {category.lastChecked} • Weight: {category.weight}%
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Critical Issues */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Critical Issues Requiring Immediate Attention</h3>
                <div className="space-y-3">
                    {Object.entries(complianceCategories)
                        .filter(([key, category]) => category.issues.length > 0)
                        .map(([key, category]) => (
                            category.issues.map((issue, index) => (
                                <div key={`${key}-${index}`} className="p-3 bg-red-50 border border-red-200 rounded-lg">
                                    <div className="flex items-start">
                                        <span className="text-red-600 mr-2">🚨</span>
                                        <div>
                                            <h4 className="font-medium text-red-800">{category.name}</h4>
                                            <p className="text-sm text-red-700">{issue}</p>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ))
                    }
                    {Object.values(complianceCategories).every(c => c.issues.length === 0) && (
                        <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-center">
                            <span className="text-green-600">✅ No critical compliance issues found</span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    const renderDeadlines = () => (
        <div className="space-y-6">
            {/* Upcoming Deadlines */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">ATO Deadlines & Requirements</h3>
                <div className="space-y-4">
                    {atoRequirements.map((requirement) => (
                        <div key={requirement.id} className="border border-gray-200 rounded-lg p-4">
                            <div className="flex justify-between items-start mb-2">
                                <div>
                                    <h4 className="font-semibold text-gray-800">{requirement.title}</h4>
                                    <p className="text-sm text-gray-600">{requirement.description}</p>
                                </div>
                                <span className={`px-3 py-1 text-xs rounded-full ${getUrgencyColor(requirement.daysRemaining, requirement.type)}`}>
                                    {requirement.daysRemaining} days
                                </span>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                                <div>
                                    <span className="text-gray-500">Due Date:</span>
                                    <span className="ml-2 font-medium">{requirement.dueDate}</span>
                                </div>
                                <div>
                                    <span className="text-gray-500">Type:</span>
                                    <span className={`ml-2 px-2 py-1 text-xs rounded ${
                                        requirement.type === 'critical' ? 'bg-red-100 text-red-800' :
                                        requirement.type === 'important' ? 'bg-orange-100 text-orange-800' :
                                        'bg-blue-100 text-blue-800'
                                    }`}>
                                        {requirement.type.toUpperCase()}
                                    </span>
                                </div>
                                <div>
                                    <span className="text-gray-500">Penalty:</span>
                                    <span className="ml-2 text-sm">{requirement.penalty}</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Compliance Calendar */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Compliance Calendar</h3>
                <div className="space-y-4">
                    {complianceCalendar.map((event) => (
                        <div key={event.id} className="border border-gray-200 rounded-lg p-4">
                            <div className="flex justify-between items-start mb-2">
                                <div>
                                    <h4 className="font-semibold text-gray-800">{event.title}</h4>
                                    <p className="text-sm text-gray-600">{event.description}</p>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <span className={`px-2 py-1 text-xs rounded ${
                                        event.completed ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                    }`}>
                                        {event.completed ? 'Completed' : 'Pending'}
                                    </span>
                                    <span className="text-xs text-gray-500">{event.frequency}</span>
                                </div>
                            </div>
                            <div className="text-sm text-gray-500">
                                Next due: {event.nextDue}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );

    const renderATORequirements = () => (
        <div className="space-y-6">
            {/* ATO Focus Areas */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Current ATO Focus Areas</h3>
                <div className="space-y-4">
                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <h4 className="font-semibold text-yellow-800 mb-2">LRBA Compliance</h4>
                        <p className="text-sm text-yellow-700">
                            ATO is focusing on Limited Recourse Borrowing Arrangements. Ensure all LRBA documentation 
                            is complete and market rent is being charged for related party rentals.
                        </p>
                    </div>
                    <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                        <h4 className="font-semibold text-orange-800 mb-2">Contribution Timing</h4>
                        <p className="text-sm text-orange-700">
                            Verify all contributions are received within the financial year they're claimed for. 
                            Late contributions may be disallowed.
                        </p>
                    </div>
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <h4 className="font-semibold text-blue-800 mb-2">Investment Strategy Documentation</h4>
                        <p className="text-sm text-blue-700">
                            Ensure investment strategy is reviewed annually and documented. Strategy should consider 
                            member circumstances and risk tolerance.
                        </p>
                    </div>
                </div>
            </div>

            {/* Compliance Checklist */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Compliance Checklist</h3>
                <div className="space-y-3">
                    {[
                        'Investment strategy reviewed and documented',
                        'Asset valuations updated and documented',
                        'Contribution caps monitored and tracked',
                        'LRBA documentation complete and current',
                        'In-house asset limits monitored',
                        'Related party transactions at market rates',
                        'Member benefit statements prepared',
                        'Annual return lodged on time',
                        'Audit completed and report filed',
                        'Trustee minutes maintained'
                    ].map((item, index) => (
                        <label key={index} className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                            <input type="checkbox" className="mr-3 text-blue-600" />
                            <span className="text-gray-700">{item}</span>
                        </label>
                    ))}
                </div>
            </div>

            {/* Penalty Information */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Penalty Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <h4 className="font-semibold text-gray-700 mb-2">Administrative Penalties</h4>
                        <ul className="text-sm text-gray-600 space-y-1">
                            <li>• Late annual return: $210 per penalty unit</li>
                            <li>• Late member statements: $210 per penalty unit</li>
                            <li>• Incomplete records: $210 per penalty unit</li>
                            <li>• Non-compliant investments: $210 per penalty unit</li>
                        </ul>
                    </div>
                    <div>
                        <h4 className="font-semibold text-gray-700 mb-2">Civil Penalties</h4>
                        <ul className="text-sm text-gray-600 space-y-1">
                            <li>• Serious contraventions: Up to $1,110,000</li>
                            <li>• Repeated breaches: Increased penalties</li>
                            <li>• Intentional non-compliance: Higher penalties</li>
                            <li>• Trustee disqualification: Possible outcome</li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderCustomRules = () => (
        <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold">Custom Compliance Rules</h3>
                    <button
                        onClick={() => setShowAddRule(!showAddRule)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                        {showAddRule ? 'Cancel' : '+ Add Custom Rule'}
                    </button>
                </div>
                
                {showAddRule && (
                    <div className="p-4 bg-gray-50 rounded-lg mb-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Rule Name</label>
                                <input
                                    type="text"
                                    value={newRule.name}
                                    onChange={(e) => setNewRule({ ...newRule, name: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    placeholder="e.g., Conservative contribution limit"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Rule Type</label>
                                <select
                                    value={newRule.type}
                                    onChange={(e) => setNewRule({ ...newRule, type: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                >
                                    <option value="threshold">Threshold</option>
                                    <option value="percentage">Percentage</option>
                                    <option value="date">Date-based</option>
                                    <option value="custom">Custom Logic</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Threshold Value</label>
                                <input
                                    {...getNumericInputProps()}
                                    value={newRule.threshold}
                                    onChange={(e) => setNewRule({ ...newRule, threshold: Number(e.target.value) || 0 })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    placeholder="e.g., 25000"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Condition</label>
                                <select
                                    value={newRule.condition}
                                    onChange={(e) => setNewRule({ ...newRule, condition: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                >
                                    <option value="greater_than">Greater than</option>
                                    <option value="less_than">Less than</option>
                                    <option value="equals">Equals</option>
                                </select>
                            </div>
                        </div>
                        <div className="mt-4 flex justify-end">
                            <button
                                onClick={() => {
                                    if (newRule.name) {
                                        setCustomRules([...customRules, { ...newRule, id: Date.now() }]);
                                        setNewRule({ name: '', type: 'threshold', threshold: 0, condition: 'greater_than', alert: true });
                                        setShowAddRule(false);
                                    }
                                }}
                                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                            >
                                Add Rule
                            </button>
                        </div>
                    </div>
                )}
                
                {customRules.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                        <p>No custom rules set</p>
                        <p className="text-sm mt-2">Add rules to set your own compliance thresholds</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {customRules.map((rule) => (
                            <div key={rule.id} className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                <div className="flex justify-between items-start">
                                    <div>
                                        <h4 className="font-semibold text-blue-800">{rule.name}</h4>
                                        <p className="text-sm text-blue-700 mt-1">
                                            Alert when {rule.type} is {rule.condition.replace('_', ' ')} ${rule.threshold.toLocaleString()}
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => setCustomRules(customRules.filter(r => r.id !== rule.id))}
                                        className="text-red-600 hover:text-red-800 text-sm"
                                    >
                                        Remove
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
    
    const renderEmailAlerts = () => (
        <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Email Alert Configuration</h3>
                
                <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div>
                            <div className="font-medium">Enable Email Alerts</div>
                            <p className="text-sm text-gray-600">Receive compliance notifications via email</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                            <input
                                type="checkbox"
                                checked={emailAlerts.enabled}
                                onChange={(e) => setEmailAlerts({ ...emailAlerts, enabled: e.target.checked })}
                                className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                    </div>
                    
                    {emailAlerts.enabled && (
                        <>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                                <input
                                    type="email"
                                    value={emailAlerts.email}
                                    onChange={(e) => setEmailAlerts({ ...emailAlerts, email: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    placeholder="your@email.com"
                                />
                            </div>
                            
                            <div className="space-y-3">
                                <h4 className="font-medium text-gray-700">Alert Preferences</h4>
                                <label className="flex items-center p-3 bg-gray-50 rounded-lg">
                                    <input
                                        type="checkbox"
                                        checked={emailAlerts.breachAlerts}
                                        onChange={(e) => setEmailAlerts({ ...emailAlerts, breachAlerts: e.target.checked })}
                                        className="mr-3 h-4 w-4 text-blue-600"
                                    />
                                    <div>
                                        <div className="font-medium">Breach Alerts</div>
                                        <div className="text-sm text-gray-600">Immediate notification when compliance breach occurs</div>
                                    </div>
                                </label>
                                <label className="flex items-center p-3 bg-gray-50 rounded-lg">
                                    <input
                                        type="checkbox"
                                        checked={emailAlerts.warningAlerts}
                                        onChange={(e) => setEmailAlerts({ ...emailAlerts, warningAlerts: e.target.checked })}
                                        className="mr-3 h-4 w-4 text-blue-600"
                                    />
                                    <div>
                                        <div className="font-medium">Warning Alerts</div>
                                        <div className="text-sm text-gray-600">Notification when approaching limits (80%+)</div>
                                    </div>
                                </label>
                                <label className="flex items-center p-3 bg-gray-50 rounded-lg">
                                    <input
                                        type="checkbox"
                                        checked={emailAlerts.monthlyReport}
                                        onChange={(e) => setEmailAlerts({ ...emailAlerts, monthlyReport: e.target.checked })}
                                        className="mr-3 h-4 w-4 text-blue-600"
                                    />
                                    <div>
                                        <div className="font-medium">Monthly Compliance Report</div>
                                        <div className="text-sm text-gray-600">Receive monthly summary of compliance status</div>
                                    </div>
                                </label>
                            </div>
                            
                            <div className="flex justify-end mt-4">
                                <button
                                    onClick={() => alert('Email alert settings saved successfully!')}
                                    className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                                >
                                    Save Alert Settings
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );

    return (
        <div className="space-y-6 p-6 bg-gray-50 rounded-lg shadow-inner">
            <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-800">Enhanced Compliance Dashboard</h2>
                <div className="flex space-x-2">
                    <button
                        onClick={() => setActiveView('dashboard')}
                        className={`px-4 py-2 rounded-md transition-colors ${
                            activeView === 'dashboard' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                        Dashboard
                    </button>
                    <button
                        onClick={() => setActiveView('deadlines')}
                        className={`px-4 py-2 rounded-md transition-colors ${
                            activeView === 'deadlines' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                        Deadlines
                    </button>
                    <button
                        onClick={() => setActiveView('ato')}
                        className={`px-4 py-2 rounded-md transition-colors ${
                            activeView === 'ato' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                        ATO Requirements
                    </button>
                    <button
                        onClick={() => setActiveView('customrules')}
                        className={`px-4 py-2 rounded-md transition-colors ${
                            activeView === 'customrules' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                        Custom Rules
                    </button>
                    <button
                        onClick={() => setActiveView('alerts')}
                        className={`px-4 py-2 rounded-md transition-colors ${
                            activeView === 'alerts' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                        Email Alerts
                    </button>
                </div>
            </div>

            {/* Content based on active view */}
            {activeView === 'dashboard' && renderDashboard()}
            {activeView === 'deadlines' && renderDeadlines()}
            {activeView === 'ato' && renderATORequirements()}
            {activeView === 'customrules' && renderCustomRules()}
            {activeView === 'alerts' && renderEmailAlerts()}

            {/* Action Buttons */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex justify-between items-center">
                    <div className="text-sm text-gray-600">
                        Last updated: {new Date().toLocaleDateString()}
                    </div>
                    <div className="flex space-x-4">
                        <button className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors">
                            Export Compliance Report
                        </button>
                        <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors">
                            Refresh Status
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SMSFEnhancedComplianceDashboard;

